import { HTMLAttributes, useState, useRef } from 'react'
import { z } from 'zod';
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { tokens } from '@/locales/translations/tokens'
import { Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { createOrganization, updateOrganization } from '@/api/organization'
import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'

type UserAuthFormProps = HTMLAttributes<HTMLDivElement>

const formSchema = z.object({
  orgName: z.string().min(1, { message: 'Nome da organização é obrigatório' }),
})

const MAX_FILE_SIZE = 5 * 1024 * 1024

export function CreateOrganizationsForm({
  className,
  ...props
}: UserAuthFormProps) {
  const { t } = useTranslation()
  const [loading, setIsLoading] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      orgName: '',
    },
  })

  const handleLogoClick = () => {
    if (logoInputRef.current) {
      logoInputRef.current.click()
    }
  }

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      const file = files[0]
      
      if (file.size > MAX_FILE_SIZE) {
        toast({
          variant: 'destructive',
          title: 'File too large',
          description: 'The maximum allowed size is 5MB',
        })
        return
      }
      
      setLogoFile(file)
    }
  }

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true)
    try {
      const response = await createOrganization({
        name: data.orgName,
      })

      if (logoFile) {
        await updateOrganization(logoFile)
      }

      toast({ title: t(tokens.ui.toasts.organization.create_success) })

      const orgId = response.organizations.external_id
      if (orgId) {
        window.location.replace(`/select-organization-plan?org_id=${orgId}`)
      } else {
        window.location.replace('/select-organization-plan')
      }
    } catch (_err) {
      toast({
        variant: 'destructive',
        title: t(tokens.ui.toasts.organization.create_error),
      })
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className='mb-6'>
            <div className='flex items-start gap-4'>
              <div>
                <div
                  className={cn(
                    'flex h-[112px] w-[112px] cursor-pointer items-center justify-center rounded-sm border border-dashed border-gray-300 bg-muted-foreground/10 transition-all duration-300 hover:bg-muted-foreground/20',
                    logoFile ? 'border-transparent' : 'border-gray-300'
                  )}
                  onClick={handleLogoClick}
                >
                  {logoFile ? (
                    <img
                      src={URL.createObjectURL(logoFile)}
                      alt='Preview'
                      className='h-full w-full rounded-sm object-cover'
                    />
                  ) : (
                    <Upload className='h-6 w-6 text-gray-400' />
                  )}
                </div>
              </div>
              <input
                type='file'
                ref={logoInputRef}
                onChange={handleLogoChange}
                className='hidden'
                accept='image/*'
              />
              <div className='mt-2 text-xs text-gray-500'>
                <Button
                  type='button'
                  variant='outline'
                  className='px-5'
                  onClick={handleLogoClick}
                >
                  {t(tokens.landing.send)}
                </Button>
                <div className='mt-2 text-xs text-gray-500'>
                  Recommend size 1:1, max. 5MB.
                </div>
              </div>
            </div>
          </div>
          <FormField
            control={form.control}
            name='orgName'
            render={({ field }) => (
              <FormItem className='space-y-1 px-1'>
                <FormControl>
                  <Input
                    placeholder={t(
                      tokens.platform.teams.organization.nameInput
                    )}
                    {...field}
                    className='w-full'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button className='mt-4 w-full' disabled={loading} type='submit'>
            {loading ? <Spinner /> : t(tokens.ui.actions.create)}
          </Button>
        </form>
      </Form>
    </div>
  )
}
